/**
 * Grabadora del paso.
 *
 * EXACTAMENTE DOS VÍAS, presentadas de forma distinta y explicadas:
 *
 *   1. Grabar nuevo  → micrófono dentro de la aplicación.
 *   2. Cargar audio  → un archivo que ya tenés.
 *
 * Sin `capture`: el selector de archivos abre archivos, no la grabadora del
 * sistema. Esa tercera vía ambigua es la confusión que la especificación pide
 * evitar.
 *
 * El audio se persiste en IndexedDB. El Object URL es solo para reproducir y
 * se reconstruye desde el Blob guardado en cada montaje.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState } from 'react';

import {
  AVISO_MUESTRA_QUIZA_AFECTADA,
  AVISO_PANTALLA_NO_SOSTUVO,
  BloqueoDePantalla,
  COMO_REHABILITAR_MICROFONO,
  ErrorDeAudio,
  LIMITE_ARCHIVO_BYTES,
  MENSAJE_ERROR,
  SesionDeGrabacion,
  duracionDeArchivo,
  extensionDe,
  formatearTamano,
  soportaGrabacion,
  normalizarMimeDeAudio,
  validarArchivoDeAudio,
  type ErrorAudio,
} from '../../app/audio';
import type { EtiquetaGrabacion } from '../../domain/modelo/grabacion';
import { grabacionesRepo } from '../../persistencia/grabacionesRepo';
import estilos from './Grabadora.module.css';

interface Props {
  sesionId: string;
  stepKey: string;
  etiqueta: EtiquetaGrabacion;
  /** Nombre por defecto de la muestra. */
  nombreSugerido: string;
  /** Minutos que el paso sugiere grabar. */
  minutosSugeridos: number | null;
}

function formatearDuracion(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function Grabadora({
  sesionId,
  stepKey,
  etiqueta,
  nombreSugerido,
  minutosSugeridos,
}: Props) {
  const grabacion = useLiveQuery(
    () => grabacionesRepo.dePaso(sesionId, stepKey),
    [sesionId, stepKey],
    undefined,
  );

  const [sesionGrabacion, setSesionGrabacion] = useState<SesionDeGrabacion | null>(null);
  const [estado, setEstado] = useState<'inactiva' | 'grabando' | 'pausada'>('inactiva');
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState<ErrorAudio | null>(null);
  const [hayBloqueo, setHayBloqueo] = useState(false);
  /**
   * La página se ocultó mientras se grababa: prueba de que el bloqueo de
   * pantalla no cumplió, diga lo que diga la API. Sobrevive a detener, para
   * poder advertir sobre la muestra ya guardada.
   */
  const [noSostuvo, setNoSostuvo] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [validando, setValidando] = useState(false);
  const [urlReproduccion, setUrlReproduccion] = useState<string | null>(null);

  const bloqueo = useRef(new BloqueoDePantalla());
  const archivoRef = useRef<HTMLInputElement>(null);

  // El Object URL se RECONSTRUYE desde el Blob persistido, y se revoca al
  // cambiar o desmontar: nunca es el almacenamiento, solo la reproducción.
  useEffect(() => {
    if (!grabacion?.blobLocal) {
      setUrlReproduccion(null);
      return;
    }
    const url = URL.createObjectURL(grabacion.blobLocal);
    setUrlReproduccion(url);
    return () => URL.revokeObjectURL(url);
  }, [grabacion?.id, grabacion?.blobLocal]);

  // Repintado del cronómetro. La cuenta sale de timestamps, no de este tick.
  useEffect(() => {
    if (estado !== 'grabando' || !sesionGrabacion) return;
    const id = setInterval(() => setSegundos(sesionGrabacion.segundos()), 500);
    return () => clearInterval(id);
  }, [estado, sesionGrabacion]);

  /**
   * Si el componente desaparece con una grabación en curso, hay que soltar el
   * micrófono y el bloqueo de pantalla. Perder la grabación es correcto: no se
   * guarda nada que el usuario no haya detenido.
   *
   * DEPENDENCIAS VACÍAS, y es lo que hace que el Wake Lock funcione.
   *
   * Antes dependía de `sesionGrabacion`. Como `empezar()` hace
   * `setSesionGrabacion(...)` ANTES de `await adquirir()`, el cambio de
   * dependencia disparaba la limpieza del efecto anterior —que llama a
   * `liberar()`— justo DESPUÉS de haber pedido el bloqueo. Orden real
   * medido: `request` → `removeEventListener` → `release`.
   *
   * Es decir: el bloqueo se soltaba solo, siempre, antes de que el usuario
   * hiciera nada. La pantalla se apagaba al tiempo de espera del sistema y
   * parecía que Android ignoraba el bloqueo. No lo ignoraba: no había
   * bloqueo. Encontrado en la prueba P sobre un teléfono real.
   *
   * La sesión se lee por `ref` para no volver a atarla a las dependencias.
   */
  const sesionActivaRef = useRef<SesionDeGrabacion | null>(null);
  sesionActivaRef.current = sesionGrabacion;

  useEffect(() => {
    const actual = bloqueo.current;
    return () => {
      sesionActivaRef.current?.cancelar();
      void actual.liberar();
    };
  }, []);

  async function empezar() {
    setError(null);
    try {
      const nueva = await SesionDeGrabacion.iniciar();
      setSesionGrabacion(nueva);
      setEstado('grabando');
      setSegundos(0);
      setNoSostuvo(false);
      // No basta con lo que responda la API: se le pasa qué hacer si se
      // comprueba que NO sostuvo.
      setHayBloqueo(await bloqueo.current.adquirir(() => setNoSostuvo(true)));
    } catch (e) {
      setError(e instanceof ErrorDeAudio ? e.tipo : 'desconocido');
    }
  }

  function pausar() {
    sesionGrabacion?.pausar();
    setEstado('pausada');
  }

  function continuar() {
    sesionGrabacion?.continuar();
    setEstado('grabando');
  }

  async function detenerYGuardar() {
    if (!sesionGrabacion) return;
    try {
      const capturada = await sesionGrabacion.detener();
      await guardar(capturada.blob, capturada.mimeType, capturada.segundos, nombreSugerido);
      setError(null);
    } catch (e) {
      setError(e instanceof ErrorDeAudio ? e.tipo : 'desconocido');
    } finally {
      await bloqueo.current.liberar();
      setSesionGrabacion(null);
      setEstado('inactiva');
      setSegundos(0);
    }
  }

  async function descartar() {
    sesionGrabacion?.cancelar();
    await bloqueo.current.liberar();
    setSesionGrabacion(null);
    setEstado('inactiva');
    setSegundos(0);
  }

  /** Guardar reemplaza: un paso tiene una muestra, no una pila de intentos. */
  async function guardar(blob: Blob, mimeType: string, duracion: number, nombre: string) {
    const previa = await grabacionesRepo.dePaso(sesionId, stepKey);
    if (previa) await grabacionesRepo.borrar(previa.id);
    await grabacionesRepo.crear({
      sesionId,
      stepKey,
      etiqueta,
      nombre,
      segundos: duracion,
      mimeType,
      blobLocal: blob,
    });
  }

  async function cargarArchivo(archivo: File) {
    setError(null);
    setValidando(true);
    // La validación puede tener que decodificar el archivo para saber si es
    // audio, así que es asíncrona. Sigue ocurriendo ANTES de tocar la base:
    // nada se guarda hasta saber que se va a poder reproducir.
    const problema = await validarArchivoDeAudio(archivo);
    setValidando(false);
    if (problema) {
      setError(problema);
      return;
    }

    // Guardar PRIMERO, averiguar la duración después. Preguntarle la duración a
    // un elemento <audio> puede tardar o no responder nunca con un archivo raro,
    // y hacer esperar al guardado por eso arriesga perder la muestra.
    //
    // El MIME se normaliza: Android devuelve `video/webm` para un archivo que
    // esta misma aplicación grabó como `audio/webm`, y guardarlo así rompería
    // la extensión de la copia descargable.
    await guardar(archivo, normalizarMimeDeAudio(archivo.type), 0, archivo.name);

    const duracion = await duracionDeArchivo(archivo);
    if (duracion > 0) {
      const guardada = await grabacionesRepo.dePaso(sesionId, stepKey);
      if (guardada) await grabacionesRepo.guardar({ ...guardada, segundos: duracion });
    }
  }

  function descargarCopia() {
    if (!grabacion?.blobLocal) return;
    const fecha = new Date().toISOString().slice(0, 10);
    const nombre = `EnglishTrainingHub_${etiqueta}_${fecha}.${extensionDe(grabacion.mimeType)}`;
    const url = URL.createObjectURL(grabacion.blobLocal);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    void grabacionesRepo.marcarDescargada(grabacion.id);
  }

  const grabando = estado !== 'inactiva';
  const puedeGrabar = soportaGrabacion();

  return (
    <section className={estilos.bloque} aria-label="Muestra de audio de este paso">
      <h3 className={estilos.titulo}>
        <span aria-hidden="true">🎧</span> Muestra de audio
      </h3>

      {minutosSugeridos !== null ? (
        <p className={estilos.sugerido}>
          Grabá alrededor de {minutosSugeridos} {minutosSugeridos === 1 ? 'minuto' : 'minutos'}.
        </p>
      ) : null}

      {/* ── Ya hay una muestra guardada ──────────────────────────────── */}
      {grabacion ? (
        <div className={estilos.guardada}>
          <p className={estilos.nombre}>
            {grabacion.nombre}
            <span className={estilos.duracion}>
              {grabacion.segundos > 0 ? formatearDuracion(grabacion.segundos) : 'duración desconocida'}
            </span>
          </p>
          {urlReproduccion ? (
            <audio className={estilos.reproductor} controls src={urlReproduccion} />
          ) : null}

          {/* La muestra ya está guardada y NO se toca: borrarla o rechazarla
              por esto sería peor. Lo que se hace es decir que hay que
              escucharla, que es lo único que puede confirmarlo. */}
          {noSostuvo && estado === 'inactiva' ? (
            <p className={estilos.avisoPantallaFuerte} role="alert">
              {AVISO_MUESTRA_QUIZA_AFECTADA}
            </p>
          ) : null}

          <div className={estilos.accionesGuardada}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={descargarCopia}
            >
              Descargar copia
            </button>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setConfirmandoBorrado(true)}
            >
              Eliminar
            </button>
          </div>

          {grabacion.descargadaManualmente ? (
            <p className={estilos.respaldada}>
              ✓ Tenés una copia guardada fuera de la aplicación.
            </p>
          ) : (
            <p className={estilos.sinRespaldo}>
              Todavía no hay copia fuera de la aplicación. Como la sincronización no
              existe aún, descargarla es lo único que la protege si el navegador borra
              sus datos.
            </p>
          )}

          {confirmandoBorrado ? (
            <div className={estilos.confirmacion} role="dialog" aria-label="Eliminar la muestra">
              <p>¿Eliminar esta muestra? Vas a tener que volver a grabarla.</p>
              <div className={estilos.acciones}>
                <button
                  type="button"
                  className={estilos.secundario}
                  onClick={() => setConfirmandoBorrado(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={estilos.peligro}
                  onClick={() => {
                    void grabacionesRepo.borrar(grabacion.id);
                    setConfirmandoBorrado(false);
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ) : null}

          <p className={estilos.reemplazar}>
            Para reemplazarla, grabá otra o cargá otro archivo: la anterior se sustituye.
          </p>
        </div>
      ) : null}

      {/* ── Grabando ─────────────────────────────────────────────────── */}
      {grabando ? (
        <div className={estilos.enCurso} role="status" aria-live="polite">
          <p className={estilos.estadoGrabacion}>
            <span className={estilos.punto} data-pausada={estado === 'pausada' || undefined} />
            {estado === 'grabando' ? 'Grabando' : 'En pausa'} · {formatearDuracion(segundos)}
          </p>

          {noSostuvo ? (
            /* Comprobado: la página se ocultó. Esto manda sobre lo que haya
               respondido la API, porque es lo que de verdad pasó. */
            <p className={estilos.avisoPantallaFuerte} role="alert">
              {AVISO_PANTALLA_NO_SOSTUVO}
            </p>
          ) : !hayBloqueo ? (
            <p className={estilos.avisoPantalla}>
              Mantené la pantalla encendida mientras grabás. Si se apaga, la grabación
              puede cortarse.
            </p>
          ) : null}

          <div className={estilos.acciones}>
            {estado === 'grabando' ? (
              <button type="button" className={estilos.secundario} onClick={pausar}>
                Pausar
              </button>
            ) : (
              <button type="button" className={estilos.secundario} onClick={continuar}>
                Continuar
              </button>
            )}
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void detenerYGuardar()}
            >
              Detener y guardar
            </button>
          </div>
          <button
            type="button"
            className={estilos.descartar}
            onClick={() => void descartar()}
          >
            Descartar
          </button>
        </div>
      ) : null}

      {/* ── Las dos vías ─────────────────────────────────────────────── */}
      {!grabando ? (
        <div className={estilos.vias}>
          <div className={estilos.via}>
            <h4 className={estilos.viaTitulo}>Grabar nuevo</h4>
            <p className={estilos.viaTexto}>
              Usá el micrófono dentro de English Training Hub.
            </p>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void empezar()}
              disabled={!puedeGrabar}
            >
              {grabacion ? 'Grabar otra vez' : 'Grabar'}
            </button>
            {!puedeGrabar ? (
              <p className={estilos.error}>{MENSAJE_ERROR.sin_soporte}</p>
            ) : null}
          </div>

          <div className={estilos.via}>
            <h4 className={estilos.viaTitulo}>Cargar audio</h4>
            <p className={estilos.viaTexto}>
              Elegí una grabación que ya tengas guardada en tu teléfono o PC.
            </p>
            <input
              ref={archivoRef}
              type="file"
              accept="audio/*,video/webm,video/ogg,video/mp4,.webm,.ogg,.oga,.m4a,.mp3,.wav,.aac,.flac"
              className="sr-only"
              aria-label="Cargar un archivo de audio"
              onChange={(evento) => {
                const archivo = evento.target.files?.[0];
                if (archivo) void cargarArchivo(archivo);
                evento.target.value = '';
              }}
            />
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => archivoRef.current?.click()}
              disabled={validando}
            >
              {validando ? 'Comprobando…' : 'Elegir archivo'}
            </button>
            {/* Comprobar puede tardar un par de segundos: hay que decodificar
                el archivo para saber si la aplicación va a poder reproducirlo.
                Sin este aviso parecería que no pasó nada al elegirlo. */}
            {validando ? (
              <p className={estilos.limite} role="status">
                Comprobando que se pueda reproducir…
              </p>
            ) : (
              <p className={estilos.limite}>
                Hasta {formatearTamano(LIMITE_ARCHIVO_BYTES)}.
              </p>
            )}

          </div>
        </div>
      ) : null}

      {error ? (
        <div className={estilos.error} role="alert">
          <p>{MENSAJE_ERROR[error]}</p>
          {error === 'permiso_denegado' ? <p>{COMO_REHABILITAR_MICROFONO}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
